import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Modal, Button } from "./ui";
import Icon from "./Icon";
import { useAuth } from "../context/AuthContext";
import { apiError, parentApi } from "../lib/api";
import { getNativePlans } from "../billing/revenueCat";

const FEATURES = ["Unlimited kids, missions & rewards", "Photo proof and parent approvals", "Streaks, badges & a crew leaderboard", "Keep every coin and adventure together"];

export default function Paywall({ open, onClose }) {
  const { family, subscribe, restorePurchases, isNativeBilling, isOwner, isPaused } = useAuth();
  const [plan, setPlan] = useState("annual");
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!open || !isOwner) return;
    let current = true;
    setPlans(null); setLoadError("");
    const request = isNativeBilling ? getNativePlans(family?.id) : parentApi.get("/billing/plans").then(({ data }) => data);
    request.then((data) => { if (current) setPlans(data); }).catch((e) => { if (current) setLoadError(apiError(e)); });
    return () => { current = false; };
  }, [open, family?.id, isNativeBilling, isOwner, reload]);

  const p = plans?.pricing || family?.pricing || {};
  const webPrice = (choice) => {
    const amount = choice === "annual" ? p.annual_total : p.founder_monthly;
    return typeof amount === "number" ? `$${amount.toFixed(2)}` : "—";
  };
  const price = (choice) => isNativeBilling ? plans?.[choice]?.priceString || "Unavailable" : webPrice(choice);
  const ready = isNativeBilling ? !!plans?.[plan]?.priceString : plans?.web_checkout_available === true;

  const go = async () => {
    setBusy(true);
    try {
      const result = await subscribe(plan);
      if (result?.cancelled || result?.redirecting) return;
      if (result?.isPlus) { toast.success("Your family's adventure is ready! 🎉"); onClose?.(); }
      else toast.message("We're confirming your subscription. Your progress is safely saved.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const result = await restorePurchases();
      if (result?.isPlus) { toast.success("Purchases restored!"); onClose?.(); }
      else toast.message("No active purchase found for this family.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="ChoreMouse Plus" testid="paywall">
      <div className="text-center mb-5">
        <div className="mx-auto mb-4 grid place-items-center w-16 h-16 rounded-3xl bg-gradient-to-br from-primary to-lavender text-white shadow-claybtn"><Icon name="rocket" width={32} height={32} /></div>
        <h2 className="font-heading font-black text-3xl text-ink leading-tight">{isPaused ? "Keep the adventure going" : "A little teamwork. Big adventures."}</h2>
        <p className="text-ink-soft font-semibold mt-2">{isPaused ? "Your trial has ended or your plan has expired. Your kids, missions and rewards are saved." : "One plan for your whole family. More motivation for every day."}</p>
      </div>
      {!isOwner ? <p className="rounded-2xl bg-sky/10 p-5 text-ink font-semibold" data-testid="paywall-owner-required">Ask the family owner to enroll and restart your adventure.</p> : <>
        <div className="grid grid-cols-2 gap-3 mb-5">
          {["annual", "monthly"].map((choice) => (
            <button key={choice} onClick={() => setPlan(choice)} disabled={busy || (isNativeBilling && !!plans && !plans[choice])} aria-pressed={plan === choice} data-testid={`plan-${choice}`}
              className={`relative text-left rounded-3xl border-2 p-4 transition-colors disabled:opacity-50 ${plan === choice ? "border-primary bg-primary/5" : "border-slate-200"}`}>
              {choice === "annual" && !isNativeBilling && p.annual_off_monthly_pct > 0 && <span className="absolute -top-2 right-2 bg-primary text-white text-[10px] font-black px-2 py-0.5 rounded-full">SAVE {p.annual_off_monthly_pct}%</span>}
              <div className="text-xs font-black uppercase tracking-wider text-ink-muted">{choice === "annual" ? "Annual" : "Monthly"}</div>
              <div className="font-heading font-black text-xl sm:text-2xl text-ink mt-1">{plans ? price(choice) : "Loading…"}</div>
              <div className="text-xs font-bold text-ink-soft">{choice === "annual" ? "per year" : "per month"}</div>
            </button>
          ))}
        </div>
        <div className="space-y-2 mb-5">
          {FEATURES.map((feature) => <div key={feature} className="flex items-center gap-2 text-ink font-semibold text-sm"><span className="w-5 h-5 rounded-full bg-primary/15 grid place-items-center text-primary shrink-0"><Icon name="check" width={13} height={13} /></span>{feature}</div>)}
        </div>
        {loadError && <div className="mb-3 text-center text-sm text-ink-soft" role="alert"><p>{loadError}</p><button onClick={() => setReload((n) => n + 1)} data-testid="paywall-retry" className="font-bold underline mt-2">Try again</button></div>}
        {!isNativeBilling && plans && !ready && <p className="rounded-2xl bg-sky/10 p-3 text-sm font-semibold text-ink-soft mb-3" data-testid="checkout-unavailable">Checkout is being set up. Your family and progress are saved; please try again soon.</p>}
        <Button size="lg" className="w-full" onClick={go} disabled={busy || !ready} data-testid="paywall-subscribe">
          {busy ? "Opening checkout…" : isNativeBilling ? "Subscribe & continue" : "Enroll & continue"}
        </Button>
        {isNativeBilling && <button onClick={restore} disabled={busy} data-testid="paywall-restore" className="w-full mt-3 text-sm font-bold text-primary-dark disabled:opacity-50">Restore purchases</button>}
        <p className="text-[11px] text-ink-muted font-semibold mt-3 text-center">{isNativeBilling ? "Your store shows the final price and renewal terms before you confirm." : "Secure checkout with Stripe. Your final total appears before you pay."} Renews automatically. Cancel anytime.</p>
      </>}
    </Modal>
  );
}
